import React, { useEffect } from 'react';
import BaseDataGrid from 'common/components/base-data-grid';
import { GridCellParams, GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { Box, Checkbox, Typography } from '@mui/material';
import { createStyleSheet } from 'utils/helpers';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from 'common/hook';
import { RootState } from 'plugins/store';
import { toast } from 'react-toastify';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import { handleError } from 'utils/helpers';
import CustomPagination from 'pages/admin/campaign/components/custom-pagination';
import CampaignCheckBoxChecked from 'pages/admin/campaign/components/icons/campaign-check-box-checked';
import CampaignCheckBoxUnchecked from '../../campaign/components/icons/campaign-check-box-uncheck';
import PaymentGridCollumn from './payment-grid-column';
import PaymentConstants from '../constants';
import { listPayments } from 'services/payment.service';
import {
    IPayment,
    IListPaymentsSearchForm,
    IListClientsPayload,
    IPaymentResponse,
} from 'types/payment';
import { setPayments } from 'slices/paymentSlice';

const PaymentDataGrid: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const payments = useAppSelector(
        (state: RootState) => state.payment.payments
    );
    const paymentsSearchForm = useAppSelector(
        (state: RootState) => state.payment.paymentsSearchForm
    );

    const [rowCount, setRowCount] = React.useState(0);
    const [paginationModel, setPaginationModel] = React.useState({
        page: 0,
        pageSize: 10,
    });

    const transformParams = (
        formState: IListPaymentsSearchForm,
        page: number
    ): IListClientsPayload => {
        const params = { ...formState };
        if (formState.paymentType?.includes(PaymentConstants.type.ALL)) {
            params.paymentType = [];
        }
        if (formState.status?.includes(PaymentConstants.status.ALL)) {
            params.status = [];
        }
        return {
            page,
            q: {
                search_with_key_cont: params.searchKeyWord,
                status_in: params.status?.join(','),
                product_type_in: params.paymentType?.join(','),
                start_date: params.startDate ?? null,
                end_date: params.endDate ?? null,
            },
        };
    };

    const fetchData = React.useCallback(
        async (page = 1) => {
            try {
                const response = await listPayments(
                    transformParams(paymentsSearchForm, page)
                );
                if (!response.success) {
                    toast.error(response.message);
                    return;
                }
                dispatch(
                    setPayments(
                        response.data.payments.map(
                            (item: IPaymentResponse) => ({
                                id: item.attributes.id,
                                productName: item.attributes.product?.name,
                                bonus: item.attributes.product?.note,
                                paymentType: item.attributes.product_type,
                                status: item.attributes.status,
                                totalAmount: item.attributes.total_price,
                                time: item.attributes.created_at_for_list,
                            })
                        )
                    )
                );
                setRowCount(response.data.total_items);
            } catch (error) {
                handleError(error);
            }
        },
        [dispatch, paymentsSearchForm]
    );

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const onClickGridViewIconButton = (row: IPayment) => {
        navigate(`/admin/payment/${row.id}`);
    };

    const columns: GridColDef[] = PaymentGridCollumn(onClickGridViewIconButton);

    function labelDisplayedRows({
        from,
        to,
        count,
    }: {
        from: number;
        to: number;
        count: number;
    }) {
        return `Xem ${from} - ${to} trên ${count !== -1 ? count : `hơn ${to}`}`;
    }

    const onCellClick = (cell: GridCellParams) => {
        if (cell.field === 'actions' || cell.field === '__check__') return;
        navigate(`/admin/payment/${cell.id}`);
    };
    return (
        <Box component="div">
            <BaseDataGrid
                onCellClick={onCellClick}
                columns={columns}
                rows={payments}
                checkboxSelection
                disableRowSelectionOnClick
                disableColumnMenu
                autoHeight
                sx={styles.dataGrid}
                rowHeight={83}
                localeText={{
                    MuiTablePagination: {
                        labelDisplayedRows,
                    },
                    footerRowSelected: (count) => (
                        <Typography component="span" fontSize={'16px'}>
                            {count} hàng đã chọn
                        </Typography>
                    ),
                }}
                slots={{
                    pagination: CustomPagination,
                    noRowsOverlay: () => (
                        <Box
                            component="div"
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            height={'100%'}
                        >
                            <Typography component="span" fontSize={'16px'}>
                                {t('page.campaign.table.noData')}
                            </Typography>
                        </Box>
                    ),
                    baseCheckbox: (props) => (
                        <Checkbox
                            {...props}
                            checkedIcon={
                                <CampaignCheckBoxChecked
                                    sx={{
                                        color: (theme) =>
                                            theme.palette.text.primary,
                                    }}
                                />
                            }
                            icon={<CampaignCheckBoxUnchecked />}
                            indeterminateIcon={
                                <IndeterminateCheckBoxIcon
                                    style={{
                                        width: '20px',
                                        height: '20px',
                                    }}
                                />
                            }
                        />
                    ),
                }}
                initialState={{
                    pagination: { paginationModel: { pageSize: 10 } },
                }}
                pageSizeOptions={[]}
                paginationMode="server"
                paginationModel={paginationModel}
                onPaginationModelChange={(newModel) => {
                    setPaginationModel(newModel);
                    fetchData(newModel.page + 1);
                }}
                rowCount={rowCount}
            />
        </Box>
    );
};

const styles = createStyleSheet({
    dataGrid: {
        '--DataGrid-rowBorderColor': 'transparent',
        '.MuiDataGrid-columnHeaders': {
            height: '83px',
            justifyContent: 'center',
        },
        '.MuiDataGrid-columnHeader': {
            padding: 0,
        },
        '.MuiDataGrid-columnHeaderTitle': {
            fontSize: '20px',
            lineHeight: '24px',
            fontWeight: 700,
        },
        '.MuiDataGrid-columnHeaderTitleContainer': {
            display: 'flex',
        },
        '.MuiDataGrid-cell': {
            outline: 'none !important',
            padding: '0',
        },
        '.MuiDataGrid-footerContainer': {
            borderColor: 'transparent',
            marginBottom: '44px',
            marginRight: '54px',
        },
        '.MuiTablePagination-displayedRows': {
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: '17px',
        },
        border: 'none',
    },
});

export default PaymentDataGrid;
